/**
 * ReportCajasPage - Reporte de efectivo y gastos de caja
 * - Cantidades de efectivo por caja (saldos actuales)
 * - Resumen detallado de gastos y egresos por usuario y por categoría
 * - Detalle cronológico con indicador de respaldo documental
 * - Exportación a Excel
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachFile,
  FileDownload,
  Search,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import dayjs from '../config/dayjs';
import { PageHeader, LoadingSpinner, EmptyState } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import * as cajaService from '../services/cajaService';
import * as catalogoService from '../services/catalogoService';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDateTime } from '../utils/formatters';

const ReportCajasPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [efectivo, setEfectivo] = useState(null);
  const [reporte, setReporte] = useState(null);
  const [cajas, setCajas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [exportando, setExportando] = useState(false);

  const esAdmin = user?.rol === 'administrador';

  // Filtros (por defecto: mes en curso)
  const [filtros, setFiltros] = useState({
    fecha_inicio: dayjs().startOf('month').format('YYYY-MM-DD'),
    fecha_fin: dayjs().format('YYYY-MM-DD'),
    caja_id: '',
    usuario_id: '',
    tipo: '',
  });

  const buildParams = () => {
    const params = {};
    Object.entries(filtros).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    return params;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [efectivoData, reporteData, cajasData, usuariosData] = await Promise.all([
        cajaService.getReporteEfectivo(),
        cajaService.getReporteMovimientos(buildParams()),
        cajaService.getCajas(),
        esAdmin ? catalogoService.getUsuarios({}).catch(() => []) : Promise.resolve([]),
      ]);
      setEfectivo(efectivoData);
      setReporte(reporteData);
      setCajas(cajasData || []);
      setUsuarios(usuariosData || []);
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      showError('Error al cargar el reporte de cajas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleExportExcel = async () => {
    try {
      setExportando(true);
      await cajaService.downloadReporteExcel(buildParams());
      showSuccess('Reporte Excel descargado');
    } catch (error) {
      showError('Error al exportar el reporte a Excel');
    } finally {
      setExportando(false);
    }
  };

  if (loading && !reporte) {
    return <LoadingSpinner message="Generando reporte de cajas..." />;
  }

  const resumen = reporte?.resumen;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Reporte de Efectivo y Gastos"
        breadcrumbs={[{ label: 'Reportes' }, { label: 'Efectivo y Gastos' }]}
        actions={[
          {
            label: exportando ? 'Exportando...' : 'Exportar Excel',
            icon: FileDownload,
            onClick: handleExportExcel,
            variant: 'outlined',
          },
        ]}
      />

      {/* ================= EFECTIVO EN CAJAS ================= */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Efectivo en Cajas
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceWallet />
                <Typography variant="body2">Total Efectivo</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {formatCurrency(efectivo?.total_efectivo || 0)}
              </Typography>
              <Typography variant="caption">
                {efectivo?.cantidad_cajas_activas || 0} caja(s) activa(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {efectivo?.cajas
          ?.filter((c) => c.activa)
          .map((caja) => (
            <Grid item xs={12} sm={6} md={3} key={caja.id}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {caja.nombre}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(caja.saldo_actual)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Hoy: +{formatCurrency(caja.ingresos_hoy)} / -{formatCurrency(caja.egresos_hoy)}
                  </Typography>
                  {caja.responsable && (
                    <Typography variant="caption" color="text.secondary">
                      Resp.: {caja.responsable}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* ================= DESGLOSE POR DENOMINACIÓN ================= */}
      {efectivo?.desglose_total && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Desglose de Efectivo por Denominación (cajas activas)
          </Typography>
          <TableContainer component={Paper} sx={{ maxWidth: 560 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Denominación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Piezas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {efectivo.desglose_total.map((d) => (
                  <TableRow key={`${d.tipo}-${d.denominacion}`} hover>
                    <TableCell>{d.etiqueta}</TableCell>
                    <TableCell>{d.tipo === 'moneda' ? 'Moneda' : 'Billete'}</TableCell>
                    <TableCell align="center">{d.cantidad}</TableCell>
                    <TableCell align="right">
                      {d.cantidad > 0 ? formatCurrency(d.subtotal) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                    TOTAL DESGLOSADO
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(
                      efectivo.desglose_total.reduce((sum, d) => sum + d.subtotal, 0)
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ================= FILTROS DEL PERÍODO ================= */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Movimientos del Período
      </Typography>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Desde"
          type="date"
          value={filtros.fecha_inicio}
          onChange={(e) => setFiltros({ ...filtros, fecha_inicio: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Hasta"
          type="date"
          value={filtros.fecha_fin}
          onChange={(e) => setFiltros({ ...filtros, fecha_fin: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          size="small"
          label="Caja"
          value={filtros.caja_id}
          onChange={(e) => setFiltros({ ...filtros, caja_id: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {cajas.map((caja) => (
            <MenuItem key={caja.id} value={caja.id}>
              {caja.nombre}
            </MenuItem>
          ))}
        </TextField>
        {esAdmin && (
          <TextField
            select
            size="small"
            label="Usuario"
            value={filtros.usuario_id}
            onChange={(e) => setFiltros({ ...filtros, usuario_id: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {usuarios.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.nombre}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Tipo"
          value={filtros.tipo}
          onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="ingreso">Ingresos</MenuItem>
          <MenuItem value="egreso">Egresos</MenuItem>
          <MenuItem value="ajuste">Ajustes</MenuItem>
        </TextField>
        <Button variant="contained" startIcon={<Search />} onClick={loadData}>
          Consultar
        </Button>
      </Box>

      {/* ================= TOTALES DEL PERÍODO ================= */}
      {resumen && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp color="success" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Ingresos
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {formatCurrency(resumen.total_ingresos)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingDown color="error" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Gastos y Egresos
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatCurrency(resumen.total_egresos)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalanceWallet color="primary" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Flujo Neto ({resumen.cantidad_movimientos} movs.)
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: resumen.flujo_neto >= 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    {formatCurrency(resumen.flujo_neto)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ================= RESÚMENES ================= */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Resumen por usuario */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Egresos e Ingresos por Usuario
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Movs.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ingresos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Egresos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumen?.resumen_por_usuario?.length > 0 ? (
                  resumen.resumen_por_usuario.map((u) => (
                    <TableRow key={u.usuario_id} hover>
                      <TableCell>
                        {u.usuario}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {u.rol}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{u.cantidad_movimientos}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(u.total_ingresos)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                        {formatCurrency(u.total_egresos)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Sin movimientos en el período
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Resumen por categoría */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Gastos por Categoría
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Movs.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumen?.resumen_por_categoria?.length > 0 ? (
                  resumen.resumen_por_categoria.map((c) => (
                    <TableRow key={c.categoria_id} hover>
                      <TableCell>{c.categoria}</TableCell>
                      <TableCell>
                        <Chip
                          label={c.tipo}
                          size="small"
                          variant="outlined"
                          color={c.tipo === 'ingreso' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">{c.cantidad_movimientos}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(c.total)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Sin movimientos en el período
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* ================= DETALLE CRONOLÓGICO ================= */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Detalle Cronológico de Movimientos
      </Typography>
      {reporte?.movimientos?.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Caja</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Beneficiario</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Comprobante</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Respaldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reporte.movimientos.map((mov) => (
                <TableRow key={mov.id} hover>
                  <TableCell>{formatDateTime(mov.fecha_movimiento)}</TableCell>
                  <TableCell>{mov.caja}</TableCell>
                  <TableCell>
                    <Chip
                      label={mov.tipo}
                      size="small"
                      color={
                        mov.tipo === 'ingreso'
                          ? 'success'
                          : mov.tipo === 'egreso'
                          ? 'error'
                          : 'warning'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{mov.categoria}</TableCell>
                  <TableCell>{mov.concepto}</TableCell>
                  <TableCell>{mov.beneficiario || '-'}</TableCell>
                  <TableCell>{mov.numero_comprobante || '-'}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: mov.tipo === 'egreso' ? 'error.main' : 'success.main',
                    }}
                  >
                    {formatCurrency(mov.monto)}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(mov.saldo_nuevo)}</TableCell>
                  <TableCell>{mov.usuario}</TableCell>
                  <TableCell align="center">
                    {mov.respaldado ? (
                      <Chip
                        icon={<AttachFile />}
                        label={mov.documentos}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Sin docs.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState message="No hay movimientos en el período seleccionado" />
      )}
    </Box>
  );
};

export default ReportCajasPage;
